import { useState } from "react";
import { Button, Select, selectState as ss, toast } from "@bfc-software/pallet";
import type { SelectMsg, SelectState } from "@bfc-software/pallet";
import { match } from "ts-pattern";
import { logger } from "@bfc-software/app-toolbox";
import { useCurrentLocalization } from "@bfc-software/bfc-web-ui/i18n";
import { assertNever } from "@bfc-software/bfc-web-ui/lib/assert-never";

import { fromQuery } from "@bfc-software/bfc-web-ui/lib/async-utils";
import {
  assignOptionsOf,
  type AssignOptions,
  type NonEmptyArray,
} from "../-data/eligibility";
import { matchesSelectorQuery } from "../-data/selector-options";
import type { AssignMode } from "../-data/task-model";
import type { ActivatedTask } from "../-data/types";
import { useAssignableSelectors } from "../-data/use-assignable-selectors";
import { isConflict, useAssignTasks } from "../-data/use-plans";
import type { SelectionState } from "../-data/use-task-selection";

interface AssignBannerProps {
  selection: SelectionState;
  onClear: () => void;
}

/** The selector the manager has picked, if any. */
type Choice = { kind: "none" } | { kind: "selector"; id: string };

/** What the Assign button can do right now. */
type AssignAction =
  | { kind: "blocked" }
  | { kind: "ready"; selectorId: string }
  | { kind: "assigning" };

/**
 * The banner a manager gets once they have ticked tasks: pick a selector, assign (or, for tasks
 * a selector already holds, reassign).
 *
 * Renders nothing for an empty selection; otherwise the body, so the three selector
 * reads only start once something is ticked. The toasts live here rather than in
 * `useAssignTasks` (as `useActivatePlan`'s live in its route), so the two tabs
 * that render the banner do not each repeat them.
 */
export function AssignBanner({ selection, onClear }: AssignBannerProps) {
  switch (selection.kind) {
    case "empty":
      return <></>;
    case "ticked":
      return (
        <AssignBannerBody
          mode={selection.mode}
          tasks={selection.tasks}
          onClear={onClear}
        />
      );
    default:
      return assertNever(selection);
  }
}

type SelectorItem = { id: string; label: string };

/**
 * Groups ride along as a label suffix: the dropdown takes flat `{id, label}`
 * items. Typing a group name therefore finds its members (the filter matches the
 * label).
 */
const toItems = (options: AssignOptions): Array<SelectorItem> =>
  options.kind === "eligible"
    ? options.selectors.map((option) => ({
        id: option.id,
        label:
          option.groups.length > 0
            ? `${option.label} (${option.groups.map((group) => group.name).join(", ")})`
            : option.label,
      }))
    : [];

/** Pure: everything the dropdown shows, from the banner's data. */
function selectorSelectState(a: {
  options: AssignOptions;
  items: ReadonlyArray<SelectorItem>;
  selectorId: string | null;
  displayLabel: string;
  text: string;
  t: (key: string) => string;
}): SelectState<SelectorItem> {
  const { options, items, selectorId, displayLabel, text, t } = a;
  const label = ss.ariaLabel(t("tasks.assign.selectorAriaLabel"));
  const live = (opts: ss.Options<SelectorItem>) => {
    const base = ss.single<SelectorItem>({
      label,
      placeholder: t("tasks.assign.selectorPlaceholder"),
      options: opts,
    });
    const picked =
      selectorId === null
        ? base
        : ss.withPick(base, { id: selectorId, label: displayLabel });
    return ss.withSearch(picked, {
      placeholder: t("tasks.assign.selectorSearchPlaceholder"),
      text,
    });
  };
  return match(options)
    .with({ kind: "nobody-eligible" }, () =>
      ss.disabled({ label, reason: t("tasks.assign.nobodyEligible") }),
    )
    .with({ kind: "failed" }, () =>
      ss.disabled({ label, reason: t("tasks.assign.optionsFailed") }),
    )
    .with({ kind: "loading" }, () =>
      live(ss.loading(t("tasks.assign.selectorLoading"))),
    )
    .with({ kind: "eligible" }, () =>
      live(
        ss.ready({
          items: items.filter((item) => matchesSelectorQuery(item, text)),
          emptyLabel: t("tasks.assign.selectorEmpty"),
        }),
      ),
    )
    .exhaustive();
}

function AssignBannerBody({
  mode,
  tasks,
  onClear,
}: {
  mode: AssignMode;
  tasks: NonEmptyArray<ActivatedTask>;
  onClear: () => void;
}) {
  const [t] = useCurrentLocalization();
  const options = assignOptionsOf(fromQuery(useAssignableSelectors()), tasks);
  const assignment = useAssignTasks();

  const [choice, setChoice] = useState<Choice>({ kind: "none" });
  const [text, setText] = useState("");

  const items = toItems(options);

  // Re-validated on every render, no effect: ticking another task can narrow the eligible list
  // under a choice made earlier, and a choice no longer on offer reads as none.
  const selectorId =
    choice.kind === "selector" && items.some((item) => item.id === choice.id)
      ? choice.id
      : null;
  const displayLabel =
    items.find((item) => item.id === selectorId)?.label ?? "";

  const action: AssignAction = assignment.isPending
    ? { kind: "assigning" }
    : selectorId !== null
      ? { kind: "ready", selectorId }
      : { kind: "blocked" };

  const onAssign = () => {
    if (action.kind !== "ready") return;
    const chosenId = action.selectorId;
    // The toast names the person, not the group suffix the dropdown draws.
    const name =
      options.kind === "eligible"
        ? (options.selectors.find((option) => option.id === chosenId)?.label ??
          chosenId)
        : chosenId;
    assignment
      .assign(
        mode,
        chosenId,
        tasks.map((task) => task.taskUlid),
      )
      .then(() => {
        toast.success(
          t(
            mode === "assign"
              ? "tasks.assign.success"
              : "tasks.assign.reassignSuccess",
            { count: tasks.length, name },
          ),
        );
        onClear();
      })
      .catch((error: unknown) => {
        logger.warn("Manual task assignment failed", {
          selectorId: chosenId,
          error,
        });
        toast.error(
          t(
            isConflict(error)
              ? mode === "assign"
                ? "tasks.assign.conflict"
                : "tasks.assign.reassignConflict"
              : mode === "assign"
                ? "tasks.assign.failed"
                : "tasks.assign.reassignFailed",
          ),
        );
        // A 409 means the page is out of date (the refetch is correcting it):
        // the ticks name rows that moved.
        if (isConflict(error)) onClear();
      });
  };

  const onMsg = (msg: SelectMsg<SelectorItem>) =>
    match(msg)
      .with({ kind: "searched" }, ({ text }) => setText(text))
      // Reset the search box, so reopening shows the full list.
      .with({ kind: "closed" }, () => setText(""))
      .with({ kind: "picked" }, ({ item }) =>
        setChoice({ kind: "selector", id: item.id }),
      )
      .with({ kind: "cleared" }, () => setChoice({ kind: "none" }))
      // `pickedN` cannot arrive from `single`
      .with({ kind: "pickedN" }, { kind: "opened" }, () => {})
      .exhaustive();

  return (
    <div
      className="flex items-center px-16 py-12 bg-surface-container border border-outline-variant"
      data-test-role="assign-banner"
    >
      <span
        className="flex-none whitespace-nowrap font-strong text-on-surface text-size-13"
        data-test-role="assign-count"
      >
        {t("tasks.assign.selected", { count: tasks.length })}
      </span>
      <Select
        state={selectorSelectState({
          options,
          items,
          selectorId,
          displayLabel,
          text,
          t,
        })}
        onMsg={onMsg}
        className="flex-1 min-w-0 ml-12"
        data-testid="assign-selector"
      />
      <div className="flex-none ml-12">
        <Button
          kind="text-only"
          variant="primary"
          text={t(
            mode === "assign" ? "tasks.assign.assign" : "tasks.assign.reassign",
          )}
          isDisabled={action.kind !== "ready"}
          onPress={onAssign}
        />
      </div>
      <div className="flex-none ml-12">
        <Button
          kind="text-only"
          variant="tertiary"
          text={t("tasks.assign.clear")}
          onPress={onClear}
        />
      </div>
    </div>
  );
}
